/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import clsx from 'clsx';

import { useStateLocalStorage } from '../useHooks';
import styles from './CollapsibleInternalContainer.module.scss';

export function CollapsibleInternalContainer({
  title,
  titleClassName,
  children,
  localStorageName,
  collapsible = true,
}: {
  children: React.ReactNode;
  title: string;
  titleClassName?: string;
  localStorageName: string;
  collapsible?: boolean;
}) {
  const [expanded, setExpanded] = useStateLocalStorage(`collapsibleInternalContainer${localStorageName}`, true);

  const handleExpandBtnClick = (event: any) => {
    event.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const preventAccordionClick = (event: any) => {
    event.preventDefault();
  };

  if (!collapsible) {
    return (
      <Box className={styles['collapsible-internal-accordion']} sx={{ p: 0 }}>
        <Box
          className={styles['collapsible-internal-accordion-summary']}
          sx={{ display: 'flex', alignItems: 'center', minHeight: '52px', paddingX: '10px' }}
        >
          <Box className={clsx(styles.accordionSummaryText, titleClassName)}>{title}</Box>
        </Box>
        <Box sx={{ padding: '8px 16px 16px' }}>{children}</Box>
      </Box>
    );
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={preventAccordionClick}
      slotProps={{ transition: { unmountOnExit: true, timeout: 150 } }}
      data-testid="internal-accordion"
      className={styles['collapsible-internal-accordion']}
      sx={{ p: 0 }}
    >
      <AccordionSummary
        expandIcon={
          <Box
            data-testid="expand-btn"
            onClick={handleExpandBtnClick}
            className={styles['collapsible-internal-accordion-expand-btn']}
          >
            {expanded ? 'close' : 'open'}
          </Box>
        }
        sx={{
          '& .MuiAccordionSummary-content': {
            margin: 0,
          },
          '&.Mui-expanded': {
            minHeight: '52px',
          },
          '.MuiAccordionSummary-content.Mui-expanded': {
            marginY: '8px',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            transform: 'none !important',
            cursor: 'pointer',
          },
          minHeight: '52px',
          paddingX: '10px',
        }}
        className={styles['collapsible-internal-accordion-summary']}
      >
        <Box className={clsx(styles.accordionSummaryText, titleClassName)}>{title}</Box>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}
